import { useEffect, useState, type FormEvent } from "react";
import { TextField } from "@/components/TextField";
import { AddressFieldset } from "@/components/AddressFieldset";
import { EMPTY_ADDRESS, formatAddress, type AddressValue } from "@/lib/address";
import { SubmitButton } from "@/components/SubmitButton";
import { useAuth } from "@/auth/AuthProvider";
import {
  getOwnBrokerage,
  updateOwnBrokerage,
  type BrokerBrokerage,
} from "@/lib/brokerAuth";
import { ApiError } from "@/lib/api";

// The broker's own brokerage (PMS-S063 / BRK-S019). Everyone may view it; only an
// owner (brokerage:edit) sees the editable form — staff get a read-only view.
// `canEdit` is derived from the broker's capabilities by the page; the server
// enforces the same rule (a staff PUT is a 403), so this only governs the UI.

function messageFor(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Something went wrong. Please try again.";
}

// Through the shared formatter (BRK-S106) — this was the second hand-rolled
// copy of the same line, and the submitted bond order would have been a third.
const addressLine = (b: BrokerBrokerage): string =>
  formatAddress(b.legalAddress);

export function BrokerageCard({ canEdit }: { canEdit: boolean }) {
  const { authedRequest } = useAuth();
  const [brokerage, setBrokerage] = useState<BrokerBrokerage | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [affiliation, setAffiliation] = useState("");
  const [address, setAddress] = useState<AddressValue>(EMPTY_ADDRESS);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  function hydrate(b: BrokerBrokerage) {
    setBrokerage(b);
    setName(b.name);
    setAffiliation(b.affiliation ?? "");
    setAddress({
      street: b.legalAddress?.street ?? "",
      city: b.legalAddress?.city ?? "",
      state: b.legalAddress?.state ?? "",
      zip: b.legalAddress?.zip ?? "",
    });
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const b = await getOwnBrokerage(authedRequest);
        if (!cancelled) hydrate(b);
      } catch (err) {
        if (!cancelled) setLoadError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setPending(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await updateOwnBrokerage(authedRequest, {
        name: name.trim(),
        affiliation: affiliation.trim() || null,
        legalAddress: {
          street: address.street.trim(),
          city: address.city.trim(),
          state: address.state.trim(),
          zip: address.zip.trim(),
        },
      });
      hydrate(updated);
      setSaved(true);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="flex flex-col gap-4 rounded-lg border border-brk-border bg-brk-panel p-5">
      <div className="flex flex-col gap-1">
        <h2 className="font-semibold">Brokerage</h2>
        <p className="text-sm text-brk-dim">
          {canEdit
            ? "Your brokerage's details. Changes apply to everyone at your brokerage."
            : "Your brokerage's details. Only an owner can edit these."}
        </p>
      </div>

      {loadError && (
        <p role="alert" className="text-sm text-brk-danger">
          {loadError}
        </p>
      )}

      {!brokerage && !loadError && (
        <p role="status" className="text-sm text-brk-dim">
          Loading your brokerage…
        </p>
      )}

      {brokerage && !canEdit && (
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Detail label="Name" value={brokerage.name} />
          <Detail label="Affiliation" value={brokerage.affiliation ?? "—"} />
          <Detail label="License number" value={brokerage.licenseNumber} />
          <Detail label="Legal address" value={addressLine(brokerage)} />
        </dl>
      )}

      {brokerage && canEdit && (
        <form className="flex flex-col gap-4" onSubmit={onSubmit}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <TextField
            label="Affiliation"
            value={affiliation}
            onChange={(e) => setAffiliation(e.target.value)}
          />
          <AddressFieldset
            title="Legal address"
            value={address}
            onChange={setAddress}
            bare
          />

          {/* License number is NIPR-verified and not broker-editable (PMS-S061). */}
          <p className="text-xs text-brk-dim">
            License number ({brokerage.licenseNumber}) is managed by Signal
            Specialty.
          </p>

          {error && (
            <p role="alert" className="text-sm text-brk-danger">
              {error}
            </p>
          )}
          {saved && (
            <p role="status" className="text-sm font-medium text-green-700">
              ✓ Brokerage saved.
            </p>
          )}

          <SubmitButton pending={pending} pendingLabel="Saving…">
            Save brokerage
          </SubmitButton>
        </form>
      )}
    </section>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-brk-dim">{label}</dt>
      <dd className="text-brk-text">{value}</dd>
    </div>
  );
}
